import { useTranslation } from "react-i18next";
import { cn } from "@core/utils";

/**
 * The dossier foundation's stepper — deliberately simpler than « Portail/Étapes » (Figma
 * `426:15144`), which draws a per-tab completion track ("la piste sous chaque libellé est le
 * remplissage de complétude de cette étape"). That richness is what the field-by-field build
 * used; the foundation rebuild asks for the opposite: the line under a tab means ONLY "this is
 * the selected tab," full stop, and completion lives in exactly one place — the single overall
 * bar below. Two sources of truth for "how much is done" is what this simplification removes.
 *
 * The four tabs are ALL real destinations now (see `_shared/types/step.ts`'s `toDossierStep`),
 * whether or not a step has fields yet — a step with nothing built shows the same honest
 * placeholder body as one that does, which is the whole point of building the shell first.
 */
export function FoundationStepper({
  sections,
  activeKey,
  onSelect,
  overallPercent,
  missingCount,
}: {
  sections: { key: string; title: string }[];
  activeKey: string;
  onSelect: (key: string) => void;
  overallPercent: number;
  missingCount: number;
}) {
  const { t } = useTranslation("acquire");

  return (
    <nav
      aria-label={t("dossier.steps.label")}
      className="border-b border-(--border-default) bg-surface-card"
    >
      <div className="mx-auto w-full max-w-285 px-md pt-md sm:px-xl">
        <ol className="flex items-start gap-lg overflow-x-auto">
          {sections.map((section) => {
            const isActive = section.key === activeKey;
            return (
              <li key={section.key} className="shrink-0">
                <button
                  type="button"
                  aria-current={isActive ? "step" : undefined}
                  onClick={() => onSelect(section.key)}
                  className="flex flex-col items-start gap-xs pb-2 outline-none"
                >
                  {/* Weight AND colour together, never colour alone — the one rule kept from the
                      richer version, because it's an accessibility rule, not a completeness one. */}
                  <span
                    className={cn(
                      "whitespace-nowrap text-sm-medium",
                      isActive
                        ? "text-sm-semibold text-(--text-primary)"
                        : "text-(--text-tertiary)"
                    )}
                  >
                    {section.title}
                  </span>
                  <span
                    aria-hidden
                    className={cn(
                      "h-0.5 w-full rounded-full",
                      isActive ? "bg-menthe-600" : "bg-transparent"
                    )}
                  />
                </button>
              </li>
            );
          })}
        </ol>

        <div className="flex flex-col gap-1.5 pb-md">
          <div className="flex items-center justify-between text-sm-medium text-(--text-secondary)">
            <span>{t("dossier.foundation.progress-label")}</span>
            <span className="text-sm-semibold text-(--text-primary)">
              {t("dossier.foundation.progress-percent", {
                percent: overallPercent,
              })}
            </span>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
            <div
              className="h-2 rounded-full bg-menthe-600 transition-[width]"
              style={{ width: `${String(overallPercent)}%` }}
            />
          </div>
          {missingCount > 0 ? (
            <p className="text-xs-regular text-(--text-tertiary)">
              {t("dossier.foundation.missing-count", { count: missingCount })}
            </p>
          ) : null}
        </div>
      </div>
    </nav>
  );
}
